'use client'

import { Fragment } from 'react'

import { InfoCircle16 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Icon, Label, Spacer, Text, Validation } from '@vinted/web-ui'
import { useIntl } from 'react-intl'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  EscrowRefundEstimateElementModel,
  EscrowRefundEstimateModel,
  RefundElementsWithAction,
} from '@marketplace-web/complaints/complaints-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

type Props = {
  isOpen: boolean
  refundEstimate: EscrowRefundEstimateModel | null | undefined
  isRefundEstimateLoading: boolean
  onCurrencyConversionClick: () => void
  onBuyerProtectionFeeInfoClick: () => void
  onSubmit: () => void
  onClose: () => void
}

const AcceptPartialRefundOfferDialogNew = ({
  isOpen,
  refundEstimate,
  isRefundEstimateLoading,
  onBuyerProtectionFeeInfoClick,
  onCurrencyConversionClick,
  onSubmit,
  onClose,
}: Props) => {
  const translate = useTranslate('accept_partial_refund_offer_dialog')
  const { locale } = useIntl()

  const { refundElements } = refundEstimate || {}

  const renderBody = () => (
    <Cell>
      <Cell
        fullWidthTitle
        styling={Cell.Styling.Tight}
        title={
          <Text
            as="h1"
            text={translate('title')}
            type={Text.Type.Heading}
            width={Text.Width.Parent}
            alignment={Text.Alignment.Center}
          />
        }
      />
      <Spacer size={Spacer.Size.Large} />
      <Cell styling={Cell.Styling.Tight} body={translate('body')} />
    </Cell>
  )

  const renderCurrencyConversionNote = () => {
    if (!refundEstimate?.beforeConversion) return null

    return (
      <button type="button" onClick={onCurrencyConversionClick}>
        <Text
          as="p"
          clickable
          theme="inverse"
          type={Text.Type.Caption}
          text={translate('learn_more_about_currency_conversion')}
        />
      </button>
    )
  }

  const renderBuyerProtectionFeeInfo = () => (
    <div className="u-flexbox">
      <Text as="span" text={translate('buyer_protection_fee_refund')} />
      <Spacer orientation={Spacer.Orientation.Vertical} />
      <button
        className="u-flexbox u-align-self-center"
        type="button"
        onClick={onBuyerProtectionFeeInfoClick}
        aria-label={translate('a11y.buyer_protection_fee_refund_info_icon')}
      >
        <Icon name={InfoCircle16} color={Icon.Color.GreyscaleLevel2} />
      </button>
    </div>
  )

  const renderRefundElement = (element: EscrowRefundEstimateElementModel) => {
    switch (element.type) {
      case RefundElementsWithAction.BuyerProtectionFee:
        return (
          <Cell
            styling={Cell.Styling.Tight}
            body={renderBuyerProtectionFeeInfo()}
            suffix={formatCurrencyAmount(element.money, locale)}
          />
        )
      default:
        return (
          <Cell
            styling={Cell.Styling.Tight}
            body={
              <>
                {element.title && <Text as="span" type={Text.Type.Subtitle} text={element.title} />}
                {element.error && <Validation text={element.error} theme="warning" />}
              </>
            }
            suffix={formatCurrencyAmount(element.money, locale)}
          />
        )
    }
  }

  const renderRefundElements = () => {
    if (!refundElements) return null

    return refundElements.map((element, index) => (
      <Fragment key={index}>{renderRefundElement(element)}</Fragment>
    ))
  }

  const renderRefundSummary = () => {
    if (!refundEstimate) return null

    return (
      <>
        <Label text={translate('summary')} type={Label.Type.Stacked} />
        <Cell>
          {renderRefundElements()}
          <Spacer size={Spacer.Size.Medium} />
          <Cell
            styling={Cell.Styling.Tight}
            title={
              refundEstimate.beforeConversion
                ? translate('total_multicurrency_refund')
                : translate('total_refund')
            }
            suffix={
              <Text
                as="h2"
                type={Text.Type.Title}
                text={formatCurrencyAmount(refundEstimate.totalEstimatedRefund, locale)}
              />
            }
          />
          {renderCurrencyConversionNote()}
        </Cell>
      </>
    )
  }

  const renderActions = () => (
    <Cell>
      <Button
        text={translate('actions.accept')}
        styling={Button.Styling.Filled}
        onClick={onSubmit}
      />
      <Spacer size={Spacer.Size.Large} />
      <Button text={translate('actions.cancel')} styling={Button.Styling.Flat} onClick={onClose} />
    </Cell>
  )

  const renderContent = () => {
    if (isRefundEstimateLoading)
      return <ContentLoader testId="accept-partial-refund-offer-dialog-loader" />

    return (
      <>
        {renderBody()}
        {renderRefundSummary()}
        {renderActions()}
      </>
    )
  }

  return (
    <Dialog show={isOpen} hasScrollableContent>
      <div className="u-fill-width">
        <ScrollableArea>{renderContent()}</ScrollableArea>
      </div>
    </Dialog>
  )
}

export default AcceptPartialRefundOfferDialogNew
